/**
 * Cloudflare Analytics Fix
 * Handles potential issues with Cloudflare Web Analytics beacon
 */

export function initCloudflareAnalyticsFix() {
  // Override the beacon API if it's causing issues
  if (typeof window !== 'undefined') {
    const originalSendBeacon = navigator.sendBeacon.bind(navigator);

    // Wrap sendBeacon to catch errors
    navigator.sendBeacon = function (url: string, data?: BodyInit | null): boolean {
      try {
        // Filter out problematic beacon requests
        if (url && url.includes('cloudflareinsights.com')) {
          // Log but don't block - just handle gracefully
          console.debug('[Analytics] Cloudflare beacon:', url);

          // Check if URL is allowed by CSP
          const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
          if (cspMeta) {
            const cspContent = cspMeta.getAttribute('content');
            if (cspContent && !cspContent.includes('cloudflareinsights.com')) {
              console.warn('[Analytics] Cloudflare beacon blocked by CSP');
              return true; // Pretend it succeeded to avoid errors
            }
          }
        }

        // Try to send the beacon
        return originalSendBeacon.call(navigator, url, data);
      } catch (error) {
        console.warn('[Analytics] Beacon error handled:', error);
        return false;
      }
    };

    // Handle fetch-based analytics as well
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      const [resource] = args;

      // Check if it's a Cloudflare Analytics request
      if (typeof resource === 'string' && resource.includes('cloudflareinsights.com')) {
        // Return a resolved promise to prevent errors
        return Promise.resolve(
          new Response('{}', {
            status: 200,
            statusText: 'OK',
            headers: new Headers({ 'content-type': 'application/json' }),
          })
        );
      }

      return originalFetch.apply(window, args);
    };

    // Also handle XMLHttpRequest for completeness
    const originalOpen = XMLHttpRequest.prototype.open.bind(XMLHttpRequest.prototype);
    XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: any[]) {
      if (typeof url === 'string' && url.includes('cloudflareinsights.com')) {
        // Replace with a dummy URL that won't cause errors
        url = 'data:application/json,{}';
      }
      return originalOpen.call(this, method, url, ...rest);
    };
  }
}

// Auto-initialize if in browser
if (typeof window !== 'undefined') {
  // Run after DOM is loaded to ensure CSP meta tags are available
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCloudflareAnalyticsFix);
  } else {
    initCloudflareAnalyticsFix();
  }
}
